import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq, isNull, ne, or, sql } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import type { Author } from "~/lib/citations/identity-resolution";
import {
  canonicalReferences,
  projectCitations,
  projectScreeningSettings,
  screeningDecisions,
} from "~/lib/db/schema";

export type ScreeningPhase = "title_abstract" | "full_text";

// ============================================================================
// Schemas
// ============================================================================

const phaseEnum = z.enum(["title_abstract", "full_text"]);

const projectIdSchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const dualScreeningDecisionSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  decision: z.enum(["include", "exclude", "maybe"]),
  exclusionCode: z.string().optional(),
  exclusionNotes: z.string().optional(),
  notes: z.string().optional(),
  timeSpentSeconds: z.number().optional(),
  phase: phaseEnum.default("title_abstract"),
});

const citationIdSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const updateDecisionSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  newDecision: z.enum(["include", "exclude", "maybe"]),
  exclusionCode: z.string().optional(),
  exclusionNotes: z.string().optional(),
  notes: z.string().optional(),
  phase: phaseEnum.default("title_abstract"),
});

// ============================================================================
// Types
// ============================================================================

export interface DualScreeningQueueCitation {
  id: string;
  screeningOrdinal: number;
  title: string;
  authors: Author[] | null;
  year: number | null;
  containerTitle: string | null;
  doi: string | null;
  pmid: string | null;
  abstract: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  consensusStatus:
    | "pending"
    | "awaiting_second"
    | "agreed_include"
    | "agreed_exclude"
    | "agreed_maybe"
    | "conflict"
    | "adjudicated";
  reviewCount: number;
}

export interface DualReviewedHistoryItem {
  citation: DualScreeningQueueCitation;
  decision: "include" | "exclude" | "maybe";
  reviewedAt: string;
  tagNames: string[];
  queuePosition: number;
}

const reviewedHistorySchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
  limit: z.number().int().min(1).max(100).default(20),
});

export interface ScreeningSettings {
  id: string;
  projectId: string;
  screeningMode: string;
  autoResolveOnAgreement: boolean;
  maybeRequiresAdjudication: boolean;
  maybeWithDecisionIsConflict: boolean;
  blindReviewers: boolean;
  adjudicatorRole: string;
  allowSelfAdjudication: boolean;
  deduplicationMode: string;
}

type ConsensusStatus =
  | "pending"
  | "awaiting_second"
  | "agreed_include"
  | "agreed_exclude"
  | "agreed_maybe"
  | "conflict"
  | "adjudicated";

type ScreeningDecision = "include" | "exclude" | "maybe";

function isUniqueConstraintError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "23505"
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get or create screening settings for a project
 */
async function getOrCreateSettings(
  db: typeof import("~/lib/db").db,
  projectId: string,
): Promise<ScreeningSettings> {
  let settings = await db.query.projectScreeningSettings.findFirst({
    where: eq(projectScreeningSettings.projectId, projectId),
  });

  if (!settings) {
    const newSettings = {
      id: nanoid(),
      projectId,
      screeningMode: "dual",
      autoResolveOnAgreement: true,
      maybeRequiresAdjudication: false,
      maybeWithDecisionIsConflict: true,
      blindReviewers: true,
      adjudicatorRole: "owner",
      allowSelfAdjudication: false,
      deduplicationMode: "automatic",
    };
    await db.insert(projectScreeningSettings).values(newSettings);
    settings = await db.query.projectScreeningSettings.findFirst({
      where: eq(projectScreeningSettings.projectId, projectId),
    });
  }

  return settings as ScreeningSettings;
}

/**
 * Determine conflict type based on decisions and settings
 */
function determineConflictType(
  d1: ScreeningDecision,
  d2: ScreeningDecision,
  settings: ScreeningSettings,
): ConsensusStatus {
  // Direct conflict: include vs exclude
  if ((d1 === "include" && d2 === "exclude") || (d1 === "exclude" && d2 === "include")) {
    return "conflict";
  }

  // Both agreed
  if (d1 === d2) {
    if (d1 === "maybe" && settings.maybeRequiresAdjudication) {
      return "conflict";
    }
    if (d1 === "include") return "agreed_include";
    if (d1 === "exclude") return "agreed_exclude";
    return "agreed_maybe";
  }

  // One maybe + one definitive
  if (d1 === "maybe" || d2 === "maybe") {
    if (settings.maybeWithDecisionIsConflict) {
      return "conflict";
    }
    // Treat maybe as agreeing with the definitive decision
    const definitive = d1 === "maybe" ? d2 : d1;
    if (definitive === "include") return "agreed_include";
    return "agreed_exclude";
  }

  // Shouldn't reach here, but default to conflict
  return "conflict";
}

/**
 * Update consensus status for a citation based on its decisions
 */
async function updateConsensusStatus(
  db: typeof import("~/lib/db").db,
  citationId: string,
  settings: ScreeningSettings,
  phase: ScreeningPhase = "title_abstract",
): Promise<void> {
  // Get active decisions for this citation filtered by phase
  const decisions = await db.query.screeningDecisions.findMany({
    where: and(
      eq(screeningDecisions.projectCitationId, citationId),
      eq(screeningDecisions.phase, phase),
      isNull(screeningDecisions.deletedAt),
    ),
    orderBy: (d, { asc }) => [asc(d.decisionOrder)],
  });

  const reviewCount = decisions.length;
  let newStatus: ConsensusStatus;
  let finalDecision: ScreeningDecision | null = null;
  let finalDecisionBy: string | null = null;

  if (settings.screeningMode === "single") {
    // Single mode: first decision finalizes
    if (reviewCount >= 1) {
      const decision = decisions[0].decision as ScreeningDecision;
      finalDecision = decision;
      finalDecisionBy = decisions[0].reviewerId;
      newStatus =
        decision === "include"
          ? "agreed_include"
          : decision === "exclude"
            ? "agreed_exclude"
            : "agreed_maybe";
    } else {
      newStatus = "pending";
    }
  } else {
    // Dual mode
    if (reviewCount === 0) {
      newStatus = "pending";
    } else if (reviewCount === 1) {
      newStatus = "awaiting_second";
    } else {
      // 2 decisions - determine consensus
      const d1 = decisions[0].decision as ScreeningDecision;
      const d2 = decisions[1].decision as ScreeningDecision;
      newStatus = determineConflictType(d1, d2, settings);

      // If agreed and auto-resolve, set final decision
      if (newStatus.startsWith("agreed_") && settings.autoResolveOnAgreement) {
        finalDecision = d1 === d2 ? d1 : d1 === "maybe" ? d2 : d1;
        // Use the second reviewer as finalDecisionBy for agreed decisions
        finalDecisionBy = decisions[1].reviewerId;
      }
    }
  }

  // Update the citation
  const updateData: Record<string, unknown> = {
    consensusStatus: newStatus,
    reviewCount,
  };

  if (finalDecision) {
    updateData.finalDecision = finalDecision;
    updateData.finalDecisionAt = new Date();
    updateData.finalDecisionBy = finalDecisionBy;
    // Also update legacy fields for backward compatibility
    updateData.screeningStatus =
      finalDecision === "include"
        ? "included"
        : finalDecision === "exclude"
          ? "excluded"
          : "maybe";
    updateData.screenedBy = finalDecisionBy;
    updateData.screenedAt = new Date();
  }

  await db
    .update(projectCitations)
    .set(updateData)
    .where(eq(projectCitations.id, citationId));
}

// ============================================================================
// Server Functions
// ============================================================================

/**
 * Get screening settings for a project
 */
export const $getScreeningSettings = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    return await getOrCreateSettings(db, data.projectId);
  });

/**
 * Update screening settings for a project
 */
export const $updateScreeningSettings = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        projectId: z.string(),
        screeningMode: z.enum(["single", "dual"]).optional(),
        autoResolveOnAgreement: z.boolean().optional(),
        maybeRequiresAdjudication: z.boolean().optional(),
        maybeWithDecisionIsConflict: z.boolean().optional(),
        blindReviewers: z.boolean().optional(),
        adjudicatorRole: z.enum(["owner", "any_member"]).optional(),
        allowSelfAdjudication: z.boolean().optional(),
        deduplicationMode: z.enum(["automatic", "manual"]).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectOwner(data.projectId, user.id);

    // Ensure settings exist
    await getOrCreateSettings(db, data.projectId);

    const { projectId, ...updates } = data;
    const filteredUpdates = Object.fromEntries(
      Object.entries(updates).filter(([, v]) => v !== undefined),
    );

    if (Object.keys(filteredUpdates).length > 0) {
      await db
        .update(projectScreeningSettings)
        .set(filteredUpdates)
        .where(eq(projectScreeningSettings.projectId, projectId));
    }

    return await getOrCreateSettings(db, projectId);
  });

/**
 * Check if current user can review a citation
 */
export const $canReviewerScreen = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Check if user already has an active decision for this citation in this phase
    const existingDecision = await db.query.screeningDecisions.findFirst({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.reviewerId, user.id),
        eq(screeningDecisions.phase, data.phase),
        isNull(screeningDecisions.deletedAt),
      ),
    });

    return { canScreen: !existingDecision };
  });

/**
 * Submit a dual-screening decision
 */
export const $submitDualScreeningDecision = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => dualScreeningDecisionSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    const settings = await getOrCreateSettings(db, data.projectId);

    try {
      await db.transaction(async (tx) => {
        // Serialize submissions for this citation to prevent decision-order races.
        const lockedCitationRows = await tx.execute(
          sql`
            SELECT ${projectCitations.id}
            FROM ${projectCitations}
            WHERE ${projectCitations.projectId} = ${data.projectId}
              AND ${projectCitations.id} = ${data.citationId}
            FOR UPDATE
          `,
        );

        if (lockedCitationRows.length === 0) {
          throw new Error("Citation not found");
        }

        const citation = await tx.query.projectCitations.findFirst({
          where: and(
            eq(projectCitations.projectId, data.projectId),
            eq(projectCitations.id, data.citationId),
          ),
        });

        if (!citation) {
          throw new Error("Citation not found");
        }

        if (
          citation.consensusStatus !== "pending" &&
          citation.consensusStatus !== "awaiting_second"
        ) {
          throw new Error("This citation is no longer available for review");
        }

        if (citation.reviewCount >= 2) {
          throw new Error("This citation already has two reviews");
        }

        const existingDecision = await tx.query.screeningDecisions.findFirst({
          where: and(
            eq(screeningDecisions.projectCitationId, data.citationId),
            eq(screeningDecisions.reviewerId, user.id),
            eq(screeningDecisions.phase, data.phase),
            isNull(screeningDecisions.deletedAt),
          ),
        });

        if (existingDecision) {
          throw new Error("You have already reviewed this citation");
        }

        const activeDecisions = await tx.query.screeningDecisions.findMany({
          where: and(
            eq(screeningDecisions.projectCitationId, data.citationId),
            eq(screeningDecisions.phase, data.phase),
            isNull(screeningDecisions.deletedAt),
          ),
        });

        if (activeDecisions.length >= 2) {
          throw new Error("This citation already has two reviews");
        }

        await tx.insert(screeningDecisions).values({
          id: nanoid(),
          projectCitationId: data.citationId,
          reviewerId: user.id,
          decision: data.decision,
          phase: data.phase,
          exclusionCode: data.decision === "exclude" ? data.exclusionCode : null,
          exclusionNotes: data.decision === "exclude" ? data.exclusionNotes : null,
          notes: data.notes,
          decisionOrder: activeDecisions.length + 1,
          isBlinded: settings.blindReviewers,
          timeSpentSeconds: data.timeSpentSeconds,
        });

        await updateConsensusStatus(
          tx as unknown as typeof import("~/lib/db").db,
          data.citationId,
          settings,
          data.phase,
        );
      });
    } catch (error) {
      if (isUniqueConstraintError(error)) {
        const latestCitation = await db.query.projectCitations.findFirst({
          where: and(
            eq(projectCitations.projectId, data.projectId),
            eq(projectCitations.id, data.citationId),
          ),
        });

        if (!latestCitation) {
          throw new Error("Citation not found");
        }

        if (
          latestCitation.consensusStatus !== "pending" &&
          latestCitation.consensusStatus !== "awaiting_second"
        ) {
          throw new Error("This citation is no longer available for review");
        }

        const existingDecision = await db.query.screeningDecisions.findFirst({
          where: and(
            eq(screeningDecisions.projectCitationId, data.citationId),
            eq(screeningDecisions.reviewerId, user.id),
            eq(screeningDecisions.phase, data.phase),
            isNull(screeningDecisions.deletedAt),
          ),
          columns: { id: true },
        });

        if (existingDecision) {
          throw new Error("You have already reviewed this citation");
        }

        throw new Error(
          "Another reviewer submitted at the same time. This citation is no longer available for your queue.",
        );
      }

      throw error;
    }

    return { success: true };
  });

/**
 * Update decision before second review (only when awaiting_second)
 */
export const $updateDecisionBeforeSecondReview = createServerFn({
  method: "POST",
})
  .inputValidator((data: unknown) => updateDecisionSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Verify citation exists and is in awaiting_second status
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    if (citation.consensusStatus !== "awaiting_second") {
      throw new Error("Can only update decision while awaiting second review");
    }

    // Find user's existing decision for this phase
    const existingDecision = await db.query.screeningDecisions.findFirst({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.reviewerId, user.id),
        eq(screeningDecisions.phase, data.phase),
        isNull(screeningDecisions.deletedAt),
      ),
    });

    if (!existingDecision) {
      throw new Error("No existing decision found to update");
    }

    // Soft-delete old decision
    await db
      .update(screeningDecisions)
      .set({
        deletedAt: new Date(),
        deletedBy: user.id,
      })
      .where(eq(screeningDecisions.id, existingDecision.id));

    const settings = await getOrCreateSettings(db, data.projectId);

    // Create new decision with same order
    await db.insert(screeningDecisions).values({
      id: nanoid(),
      projectCitationId: data.citationId,
      reviewerId: user.id,
      decision: data.newDecision,
      phase: data.phase,
      exclusionCode: data.newDecision === "exclude" ? data.exclusionCode : null,
      exclusionNotes: data.newDecision === "exclude" ? data.exclusionNotes : null,
      notes: data.notes,
      decisionOrder: existingDecision.decisionOrder,
      isBlinded: settings.blindReviewers,
    });

    // Consensus status should remain awaiting_second
    await updateConsensusStatus(db, data.citationId, settings, data.phase);

    return { success: true };
  });

/**
 * Undo a dual-screening decision (soft delete)
 */
export const $undoDualScreeningDecision = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Verify citation exists
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    // Cannot undo finalized decisions
    const finalStatuses = [
      "agreed_include",
      "agreed_exclude",
      "agreed_maybe",
      "adjudicated",
    ];
    if (finalStatuses.includes(citation.consensusStatus ?? "")) {
      throw new Error("Cannot undo decision after consensus is reached");
    }

    // Find user's decision for this phase
    const existingDecision = await db.query.screeningDecisions.findFirst({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.reviewerId, user.id),
        eq(screeningDecisions.phase, data.phase),
        isNull(screeningDecisions.deletedAt),
      ),
    });

    if (!existingDecision) {
      throw new Error("No decision found to undo");
    }

    // Soft-delete the decision
    await db
      .update(screeningDecisions)
      .set({
        deletedAt: new Date(),
        deletedBy: user.id,
      })
      .where(eq(screeningDecisions.id, existingDecision.id));

    // Recalculate consensus status
    const settings = await getOrCreateSettings(db, data.projectId);
    await updateConsensusStatus(db, data.citationId, settings, data.phase);

    return { success: true };
  });

/**
 * Get dual-screening queue (citations not yet reviewed by current user)
 */
export const $getDualScreeningQueue = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Get citations that:
    // 1. Are in pending or awaiting_second status
    // 2. Have NOT been reviewed by current user
    // 3. Are NOT confirmed duplicates
    // 4. Match the requested screening phase
    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        or(
          eq(projectCitations.consensusStatus, "pending"),
          eq(projectCitations.consensusStatus, "awaiting_second"),
        ),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      with: {
        canonicalReference: true,
      },
      orderBy: (citations, { asc }) => [asc(citations.screeningOrdinal)],
    });

    // Filter out citations already reviewed by current user in this phase
    const citationIds = citations.map((c) => c.id);
    const userDecisions =
      citationIds.length > 0
        ? await db.query.screeningDecisions.findMany({
            where: and(
              sql`${screeningDecisions.projectCitationId} IN (${sql.join(
                citationIds.map((id) => sql`${id}`),
                sql`, `,
              )})`,
              eq(screeningDecisions.reviewerId, user.id),
              eq(screeningDecisions.phase, data.phase),
              isNull(screeningDecisions.deletedAt),
            ),
          })
        : [];

    const reviewedCitationIds = new Set(userDecisions.map((d) => d.projectCitationId));

    const filteredCitations = citations.filter((c) => !reviewedCitationIds.has(c.id));

    return filteredCitations.map((c) => ({
      id: c.id,
      screeningOrdinal: c.screeningOrdinal,
      title: c.canonicalReference.title,
      authors: c.canonicalReference.authors as Author[] | null,
      year: c.canonicalReference.year,
      containerTitle: c.canonicalReference.containerTitle,
      doi: c.canonicalReference.doi,
      pmid: c.canonicalReference.pmid,
      abstract: c.canonicalReference.abstract,
      volume: c.canonicalReference.volume,
      issue: c.canonicalReference.issue,
      pages: c.canonicalReference.pages,
      consensusStatus: c.consensusStatus as DualScreeningQueueCitation["consensusStatus"],
      reviewCount: c.reviewCount,
    })) satisfies DualScreeningQueueCitation[];
  });

/**
 * Get recently reviewed citations for the current user (dual-screening mode).
 * Includes only active decisions for the requested phase.
 */
export const $getDualReviewedHistory = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => reviewedHistorySchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const rows = await db
      .select({
        citationId: projectCitations.id,
        screeningOrdinal: projectCitations.screeningOrdinal,
        consensusStatus: projectCitations.consensusStatus,
        reviewCount: projectCitations.reviewCount,
        title: canonicalReferences.title,
        authors: canonicalReferences.authors,
        year: canonicalReferences.year,
        containerTitle: canonicalReferences.containerTitle,
        doi: canonicalReferences.doi,
        pmid: canonicalReferences.pmid,
        abstract: canonicalReferences.abstract,
        volume: canonicalReferences.volume,
        issue: canonicalReferences.issue,
        pages: canonicalReferences.pages,
        decision: screeningDecisions.decision,
        reviewedAt: screeningDecisions.createdAt,
      })
      .from(screeningDecisions)
      .innerJoin(
        projectCitations,
        eq(screeningDecisions.projectCitationId, projectCitations.id),
      )
      .innerJoin(
        canonicalReferences,
        eq(projectCitations.canonicalReferenceId, canonicalReferences.id),
      )
      .where(
        and(
          eq(projectCitations.projectId, data.projectId),
          eq(projectCitations.screeningPhase, data.phase),
          eq(screeningDecisions.reviewerId, user.id),
          eq(screeningDecisions.phase, data.phase),
          isNull(screeningDecisions.deletedAt),
          or(
            ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
            isNull(projectCitations.duplicateStatus),
          ),
        ),
      )
      .orderBy(desc(screeningDecisions.createdAt))
      .limit(data.limit);

    return rows.map((row) => ({
      citation: {
        id: row.citationId,
        screeningOrdinal: row.screeningOrdinal,
        title: row.title,
        authors: row.authors as Author[] | null,
        year: row.year,
        containerTitle: row.containerTitle,
        doi: row.doi,
        pmid: row.pmid,
        abstract: row.abstract,
        volume: row.volume,
        issue: row.issue,
        pages: row.pages,
        consensusStatus:
          row.consensusStatus as DualScreeningQueueCitation["consensusStatus"],
        reviewCount: row.reviewCount,
      },
      decision: row.decision as DualReviewedHistoryItem["decision"],
      reviewedAt: row.reviewedAt.toISOString(),
      tagNames: [],
      queuePosition: row.screeningOrdinal,
    })) satisfies DualReviewedHistoryItem[];
  });

/**
 * Get dual-screening stats
 */
export const $getDualScreeningStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      columns: {
        consensusStatus: true,
        finalDecision: true,
      },
    });

    const stats = {
      total: citations.length,
      pending: 0,
      awaitingSecond: 0,
      agreedInclude: 0,
      agreedExclude: 0,
      agreedMaybe: 0,
      conflict: 0,
      adjudicated: 0,
    };

    for (const c of citations) {
      switch (c.consensusStatus) {
        case "pending":
          stats.pending++;
          break;
        case "awaiting_second":
          stats.awaitingSecond++;
          break;
        case "agreed_include":
          stats.agreedInclude++;
          break;
        case "agreed_exclude":
          stats.agreedExclude++;
          break;
        case "agreed_maybe":
          stats.agreedMaybe++;
          break;
        case "conflict":
          stats.conflict++;
          break;
        case "adjudicated":
          stats.adjudicated++;
          break;
      }
    }

    return stats;
  });
